import React from 'react';
import styled from '@emotion/styled';
import { RefreshCw } from 'lucide-react';
import { colors } from '../../theme/colors';

const ContentSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContentTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const FieldRerunSection = ({ 
  currentValues, 
  lastProcessed, 
  isProcessing, 
  onReRun,
  renderStatusBar 
}) => {
  return (
    <>
      {renderStatusBar && renderStatusBar()}
      <ContentSection>
        <ContentTitle>Raw Field Extraction</ContentTitle>
        <div style={{ 
          background: '#fef3c7', 
          border: '1px solid #f59e0b', 
          borderRadius: '8px', 
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem', 
            marginBottom: '1rem' 
          }}>
            <div style={{ 
              width: '24px', 
              height: '24px', 
              borderRadius: '50%', 
              background: '#f59e0b', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              ⚠️
            </div>
            <h4 style={{ 
              margin: 0, 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              color: '#92400e' 
            }}>
              What happens when you re-run?
            </h4>
          </div>
          <div style={{ color: '#92400e', fontSize: '0.875rem', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 1rem 0' }}>
              <strong>Re-running will:</strong>
            </p>
            <ul style={{ margin: '0 0 1rem 0', paddingLeft: '1.5rem' }}>
              <li>Re-process the field extraction from ALL documents in the loan package</li>
              <li>Display raw extraction results from each document (if found)</li>
              <li>Show what was extracted without applying document priority rules</li>
              <li>Ignore matching algorithms - show pure extraction results</li>
              <li>Update the last processed timestamp</li>
            </ul>
            <p style={{ margin: '0 0 1rem 0' }}>
              <strong>Note:</strong> This is a manual diagnostic tool. It will show you exactly 
              what was found in each document without any processing rules applied. 
              Manual overrides and current field status will remain unchanged.
            </p>
          </div>
        </div>
        
        <div style={{ 
          background: '#f0f9ff', 
          border: '1px solid #0ea5e9', 
          borderRadius: '8px', 
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ 
            margin: '0 0 1rem 0', 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#0c4a6e' 
          }}>
            Current Field Status
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Extracted Value
              </div>
              <div style={{ color: '#6b7280', fontFamily: 'monospace' }}>
                {currentValues.value}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Master Value
              </div>
              <div style={{ color: '#6b7280', fontFamily: 'monospace' }}>
                {currentValues.master_value}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Source Document
              </div>
              <div style={{ color: '#6b7280' }}>
                {currentValues.source}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Last Processed
              </div>
              <div style={{ color: '#6b7280' }}>
                {new Date(lastProcessed).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onReRun}
            disabled={isProcessing}
            data-cy="rerun-field-button"
            style={{
              padding: '0.75rem 2rem',
              background: isProcessing ? '#9ca3af' : colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              margin: '0 auto',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isProcessing) {
                e.target.style.background = colors.primaryHover;
              }
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) {
                e.target.style.background = colors.primary;
              }
            }}
          >
            <RefreshCw size={20} className={isProcessing ? 'animate-spin' : ''} />
            {isProcessing ? 'Extracting from documents...' : 'Re-run Raw Extraction'}
          </button>
        </div>
      </ContentSection>
    </>
  );
};

export default FieldRerunSection;
